import React, { useState } from "react";

import Dropzone from "react-dropzone";
import { Circle, Image as ImageIcon } from "react-feather";

import { Button, ImageAspectRatio, ImageCropper, SectionCard } from "shared/ui";
import {
    MENU_COVER_MAX_BYTES,
    MENU_LOGO_MAX_BYTES,
    validateImageFile,
} from "shared/lib/mediaUpload";
import { uploadMenuCover, uploadMenuLogo } from "services/uploads/rest";

import { useMenuSettingsContext } from "features/MenuSettings/context";

import {
    Actions,
    CoverImage,
    CoverPlaceholder,
    LogoBadge,
    LogoImage,
    PlaceholderText,
    Preview,
    PreviewRow,
} from "../styles";

type ImageKind = "cover" | "logo";

const MAX_BYTES: Record<ImageKind, number> = {
    cover: MENU_COVER_MAX_BYTES,
    logo: MENU_LOGO_MAX_BYTES,
};

/*
* Показываем обложку и логотип так же, как их видит гость и как они выглядят в списке
* меню: баннер с круглым логотипом в углу. Отдельные крупные загрузчики занимали пол-экрана
* и создавали ложное впечатление о реальном размере картинок.
* */
export const MenuCoverCard = () => {
    const { appearance, notify } = useMenuSettingsContext();

    const [kind, setKind] = useState<ImageKind | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);

    const { backgroundPhotoUrl, logoPhotoUrl } = appearance.photos;

    const pickFile = (nextKind: ImageKind) => (files: File[]) => {
        if (files.length === 0) return;

        const error = validateImageFile(files[0], MAX_BYTES[nextKind]);

        if (error !== null) {
            notify.showError(error);
            return;
        }

        setKind(nextKind);
        setFile(files[0]);
    };

    const closeCropper = () => {
        setFile(null);
        setKind(null);
    };

    const handleUpload = async (cropped: File) => {
        setUploading(true);
        try {
            if (kind === "logo") {
                const result = await uploadMenuLogo(cropped);
                appearance.setLogoPhotoUrl(result.url);
            } else {
                const result = await uploadMenuCover(cropped);
                appearance.setBackgroundPhotoUrl(result.url);
            }
        } catch (e) {
            notify.showError("Не удалось загрузить изображение", e);
        } finally {
            setUploading(false);
            closeCropper();
        }
    };

    return (
        <SectionCard
            title="Обложка и логотип"
            description="Так меню выглядит у гостя при открытии."
        >
            <PreviewRow>
                <Preview>
                    {backgroundPhotoUrl ? (
                        <CoverImage src={backgroundPhotoUrl} alt="" />
                    ) : (
                        <CoverPlaceholder>
                            <ImageIcon size={32} />
                            <PlaceholderText>Обложка не загружена</PlaceholderText>
                        </CoverPlaceholder>
                    )}
                    <LogoBadge>
                        {logoPhotoUrl ? (
                            <LogoImage src={logoPhotoUrl} alt="" />
                        ) : (
                            <Circle size={20} />
                        )}
                    </LogoBadge>
                </Preview>

                <Actions>
                    <Dropzone onDrop={pickFile("cover")}>
                        {({ getRootProps, getInputProps }) => (
                            <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                <Button variant="outlined" disabled={uploading} fullWidth>
                                    {backgroundPhotoUrl ? "Заменить обложку" : "Загрузить обложку"}
                                </Button>
                            </div>
                        )}
                    </Dropzone>

                    <Dropzone onDrop={pickFile("logo")}>
                        {({ getRootProps, getInputProps }) => (
                            <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                <Button variant="outlined" disabled={uploading} fullWidth>
                                    {logoPhotoUrl ? "Заменить логотип" : "Загрузить логотип"}
                                </Button>
                            </div>
                        )}
                    </Dropzone>
                </Actions>
            </PreviewRow>

            <ImageCropper
                open={!!file}
                onClose={closeCropper}
                image={file}
                onCrop={handleUpload}
                aspectRatio={
                    kind === "logo"
                        ? ImageAspectRatio.square
                        : ImageAspectRatio.rectangle_3_2
                }
            />
        </SectionCard>
    );
};
